import { defineStore } from 'pinia'
import { PromotionalEventsService } from '@/api/services/PromotionalEventsService'
import { getWeeklyLeaderboard, getTeamStatus, switchTeam } from '@/services/api/requests/promotionalEvents'
import { useAppStore } from '@/store/app'
import { useProfileStore } from '@/store/profile'
import type {
  PromotionalEventResponse,
  UserWPStats,
  LeaderboardEntry,
  EventHistoryResponse,
  PromotionalEventStatus,
  TeamStatusResponse,
} from '@/types/promotionalEvents'

interface PromotionalEventsState {
  // Current Event
  currentEvent: PromotionalEventResponse | null
  currentEventLoading: boolean
  currentEventError: string | null

  // User Stats (when authenticated)
  userStats: UserWPStats | null
  userStatsLoading: boolean
  userStatsError: string | null

  // Leaderboard
  leaderboard: LeaderboardEntry[]
  leaderboardLoading: boolean
  leaderboardError: string | null
  leaderboardHasMore: boolean

  // Weekly Leaderboard
  weeklyLeaderboard: LeaderboardEntry[]
  weeklyLeaderboardLoading: boolean
  weeklyLeaderboardError: string | null
  weeklyLeaderboardRequestedDate: string | null
  weeklyLeaderboardWeekStartDate: string | null

  // Team Status
  teamStatus: TeamStatusResponse | null
  teamStatusLoading: boolean
  teamStatusError: string | null

  // History (when authenticated)
  eventHistory: EventHistoryResponse['events']
  historyLoading: boolean
  historyError: string | null

  // Real-time updates
  lastUpdate: Date | null
}

export const usePromotionalEventsStore = defineStore('promotionalEvents', {
  state: (): PromotionalEventsState => ({
    // Current Event
    currentEvent: null,
    currentEventLoading: false,
    currentEventError: null,

    // User Stats
    userStats: null,
    userStatsLoading: false,
    userStatsError: null,

    // Leaderboard
    leaderboard: [],
    leaderboardLoading: false,
    leaderboardError: null,
    leaderboardHasMore: false,

    // Weekly Leaderboard
    weeklyLeaderboard: [],
    weeklyLeaderboardLoading: false,
    weeklyLeaderboardError: null,
    weeklyLeaderboardRequestedDate: null,
    weeklyLeaderboardWeekStartDate: null,

    // Team Status
    teamStatus: null,
    teamStatusLoading: false,
    teamStatusError: null,

    // History
    eventHistory: [],
    historyLoading: false,
    historyError: null,

    // Real-time
    lastUpdate: null,
  }),

  getters: {
    hasActiveEvent: (state) => state.currentEvent?.status === 2, // ACTIVE
    hasEvent: (state) => state.currentEvent !== null, // Has either active or past event
    isPastEvent: (state) => state.currentEvent?.status === 3, // ENDED
    timeRemaining: (state) => {
      if (!state.currentEvent?.endsAt) return null
      const now = new Date()
      const end = new Date(state.currentEvent.endsAt)
      const diff = end.getTime() - now.getTime()
      return diff > 0 ? diff : 0
    },
    userRank: (state) => state.userStats?.currentPosition || null,
  },

  actions: {
    // === Current Event ===
    fetchCurrentEventFromInit() {
      const appStore = useAppStore()
      this.currentEvent = appStore.initialData?.currentEvent || null
      this.lastUpdate = new Date()
    },

    // === User Stats ===
    fetchUserStatsFromProfile() {
      const profileStore = useProfileStore()
      this.userStats = profileStore.eventStats || null
      this.lastUpdate = new Date()
    },

    // === Leaderboard ===
    async fetchLeaderboard() {
      if (!this.currentEvent?.id) return

      this.leaderboardLoading = true
      this.leaderboardError = null

      try {
        const leaderboardResponse =
          await PromotionalEventsService.getV1PromotionalEventsLeaderboard(this.currentEvent.id)
        this.leaderboard = leaderboardResponse.leaderboard
        this.leaderboardHasMore = false // No pagination, always top 100
        this.lastUpdate = new Date()
      } catch (error) {
        this.leaderboardError =
          error instanceof Error ? error.message : 'Failed to fetch leaderboard'
        console.error('Failed to fetch leaderboard:', error)
      } finally {
        this.leaderboardLoading = false
      }
    },

    // === Weekly Leaderboard ===
    async fetchWeeklyLeaderboard(date?: string) {
      if (!this.currentEvent?.id) return

      this.weeklyLeaderboardLoading = true
      this.weeklyLeaderboardError = null
      this.weeklyLeaderboardRequestedDate = date ?? null
      this.weeklyLeaderboardWeekStartDate = null

      try {
        const response = await getWeeklyLeaderboard(this.currentEvent.id, date)
        this.weeklyLeaderboard = response.leaderboard
        this.weeklyLeaderboardWeekStartDate = response.weekStartDate
        this.lastUpdate = new Date()
      } catch (error) {
        this.weeklyLeaderboardError =
          error instanceof Error ? error.message : 'Failed to fetch weekly leaderboard'
        console.error('Failed to fetch weekly leaderboard:', error)
      } finally {
        this.weeklyLeaderboardLoading = false
      }
    },

    // === Team Status ===
    async fetchTeamStatus() {
      this.teamStatusLoading = true
      this.teamStatusError = null

      try {
        const response = await getTeamStatus()
        this.teamStatus = response
        this.lastUpdate = new Date()
      } catch (error) {
        this.teamStatusError =
          error instanceof Error ? error.message : 'Failed to fetch team status'
        console.error('Failed to fetch team status:', error)
      } finally {
        this.teamStatusLoading = false
      }
    },

    async switchUserTeam() {
      this.teamStatusError = null

      try {
        const response = await switchTeam()
        if (this.teamStatus?.user) {
          this.teamStatus.user.team = response.newTeam
          this.teamStatus.user.teamSwitchUsed = response.teamSwitchUsed
        }
        this.lastUpdate = new Date()
        return response
      } catch (error) {
        this.teamStatusError =
          error instanceof Error ? error.message : 'Failed to switch team'
        console.error('Failed to switch team:', error)
        throw error
      }
    },

    // === Event History ===
    async fetchEventHistory(limit = 10, offset = 0) {
      this.historyLoading = true
      this.historyError = null

      try {
        const response = await PromotionalEventsService.getV1PromotionalEventsHistory(limit, offset)
        this.eventHistory = response.events
      } catch (error) {
        this.historyError = error instanceof Error ? error.message : 'Failed to fetch event history'
        console.error('Failed to fetch event history:', error)
      } finally {
        this.historyLoading = false
      }
    },

    // === Real-time Updates ===
    updateWPStats(newStats: Partial<UserWPStats>) {
      if (this.userStats) {
        this.userStats = { ...this.userStats, ...newStats }
        this.lastUpdate = new Date()
      }
    },

    updateLeaderboard(newLeaderboard: LeaderboardEntry[]) {
      this.leaderboard = newLeaderboard
      this.lastUpdate = new Date()
    },

    // === Event Lifecycle ===
    onEventActivated(event: PromotionalEventResponse) {
      this.currentEvent = event
      // Reset stats and leaderboard for new event
      this.userStats = null
      this.leaderboard = []
      this.lastUpdate = new Date()
    },

    onEventEnded() {
      if (this.currentEvent) {
        this.currentEvent.status = 3 // ENDED
        this.lastUpdate = new Date()
      }
    },

    // === Utility ===
    async initializeEventData() {
      // Get current event from app store (init data)
      this.fetchCurrentEventFromInit()

      if (this.hasEvent) {
        // Fetch leaderboard for any event (active or past)
        await this.fetchLeaderboard()

        // Only fetch user stats for active events
        if (this.hasActiveEvent) {
          this.fetchUserStatsFromProfile()
          // Fetch team status for active events (includes daily bonuses)
          this.fetchTeamStatus()
        }
      }
    },

    clearEventData() {
      this.currentEvent = null
      this.userStats = null
      this.leaderboard = []
      this.weeklyLeaderboard = []
      this.weeklyLeaderboardLoading = false
      this.weeklyLeaderboardError = null
      this.weeklyLeaderboardRequestedDate = null
      this.weeklyLeaderboardWeekStartDate = null
      this.teamStatus = null
      this.teamStatusError = null
      this.teamStatusLoading = false
      this.eventHistory = []
      this.lastUpdate = null
    },
  },
})
